import React, { useEffect, useState } from 'react';
import {
    generatePath,
    Redirect,
    useHistory,
    useParams,
} from 'react-router-dom';

import {
    fetchDefaultTicketVisibilityForQueue,
    setDefaultTicketVisibilityForQueue,
} from 'flow/actions/tickets-action';
import Page from 'flow/components/Setting/Page';
import Divider from 'flow/components/ui-components/Divider';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectHasTicketsSetAccessLevel } from 'flow/selectors/feature-flag-selectors';
import { selectQueueEditable } from 'flow/selectors/queue-selectors';
import {
    selectDefaultTicketVisibilityForQueue,
    selectQueueIsEligibleForTicketsConfiguration,
} from 'flow/selectors/TicketsSelectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TicketVisibility } from 'flow/types/Tickets';
import {
    getTicketVisibilityGrantI18Key,
    getTicketVisibilityNameI18Key,
} from 'flow/utils/ticket-utils';

const VISIBILITY_OPTIONS: TicketVisibility[] = [
    'private',
    'restricted',
    'public',
];

const PbxTicketsSettingsVisibility = () => {
    const { i18next } = useAppContext();
    const { entityKey: queueEntityKey } = useParams<{ entityKey: QueueKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const hasTicketsSetAccessLevel = useAppSelector(
        selectHasTicketsSetAccessLevel,
    );

    const queueIsEligibleForTicketsConfiguration = useAppSelector((state) =>
        selectQueueIsEligibleForTicketsConfiguration(state, queueEntityKey),
    );

    const userCanEditQueue = useAppSelector((state) =>
        selectQueueEditable(state, queueEntityKey),
    );

    const userCanActivateTicketsOnQueue =
        queueIsEligibleForTicketsConfiguration && userCanEditQueue;

    const defaultVisibility = useAppSelector((state) =>
        selectDefaultTicketVisibilityForQueue(state, queueEntityKey),
    );

    const [isPerformingApiRequest, setIsPerformingApiRequest] = useState(false);

    // Reached by deep link as well as from the tickets settings page, so it
    // cannot rely on the parent having fetched. The effect still runs on the
    // render that redirects, so the flag has to gate the fetch as well.
    useEffect(() => {
        if (!hasTicketsSetAccessLevel) return;

        setIsPerformingApiRequest(true);
        dispatch(fetchDefaultTicketVisibilityForQueue(queueEntityKey)).finally(
            () => {
                setIsPerformingApiRequest(false);
            },
        );
    }, [dispatch, queueEntityKey, hasTicketsSetAccessLevel]);

    // Nothing links here while the flag is off, but a bookmark or a stale link
    // still reaches the page. Send those back to the queue's ticket settings.
    if (!hasTicketsSetAccessLevel) {
        return (
            <Redirect
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.tickets'],
                    {
                        entityKey: queueEntityKey,
                    },
                )}
            />
        );
    }

    const handleChange = (value: TicketVisibility) => {
        setIsPerformingApiRequest(true);
        dispatch(
            setDefaultTicketVisibilityForQueue(queueEntityKey, value),
        ).finally(() => {
            setIsPerformingApiRequest(false);
        });
    };

    return (
        <Page
            header={i18next.t(
                'ticket.settings.queue.default.non.member.access',
            )}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.queues.entityKey.settings.tickets'],
                        {
                            entityKey: queueEntityKey,
                        },
                    ),
                )
            }
        >
            <>
                <Divider />
                {VISIBILITY_OPTIONS.map((visibility) => (
                    <SettingSelectableItem
                        key={visibility}
                        value={visibility}
                        onClick={handleChange}
                        selected={defaultVisibility}
                        description={i18next.t(
                            getTicketVisibilityGrantI18Key(visibility),
                        )}
                        isDisabled={
                            !userCanActivateTicketsOnQueue ||
                            isPerformingApiRequest
                        }
                    >
                        {i18next.t(getTicketVisibilityNameI18Key(visibility))}
                    </SettingSelectableItem>
                ))}
                <Divider />
            </>
        </Page>
    );
};

export default PbxTicketsSettingsVisibility;
